/**
 * Single source of truth for VoiceBot operational mode and activation.
 * Shared by avatar and Fluency iframes; toggles from Avatar Tuning Panel.
 */

import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { resolveOperationalMode, VOICEBOT_MODES } from './voiceBotOperationalMode';
import { buildActivation } from './voiceBotActivation';
import {
  CLASSIFIER_MODES,
  normalizeClassifierMode,
  normalizeRenderFps,
  normalizeVerifiedMix,
  normalizeLegacyGearPasser,
  normalizeLegacyVisemeFilter,
  normalizeLegacyConsonantHolder,
  normalizeFaceMorph,
  PREFERRED_ENGINE_START,
} from './voiceBotExperimentConfig';
import {
  ENVELOPE_SIM_PROFILES,
  normalizeSimProfile,
  readEnvelopeUrlFlags,
} from './voiceBotEnvelopeConfig';
import { startVoiceBotEnvelope } from './voiceBotEnvelope';
import { startVoiceBotSimHarness, stopVoiceBotSimHarness } from './voiceBotSimHarness';
import { isJourneyArmed } from './voiceBotJourney';

const BENCH_TOGGLE_KEY = 'voiceBotBenchEnabled';
const RECORD_TOGGLE_KEY = 'voiceBotRecordEnabled';
const REPLAY_TOGGLE_KEY = 'voiceBotReplayEnabled';
const REGRESSION_MODE_KEY = 'voiceBotRegressionMode';
const CLASSIFIER_MODE_KEY = 'voiceBotClassifierMode';
const RENDER_FPS_KEY = 'voiceBotRenderFps';
const VERIFIED_MIX_KEY = 'voiceBotVerifiedMix';
const LEGACY_GEAR_PASSER_KEY = 'voiceBotLegacyGearPasser';
const LEGACY_VISEME_FILTER_KEY = 'voiceBotLegacyVisemeFilter';
const LEGACY_CONSONANT_HOLDER_KEY = 'voiceBotLegacyConsonantHolder';
const FACE_MORPH_KEY = 'voiceBotFaceMorph';
const ENVELOPE_TOGGLE_KEY = 'voiceBotEnvelopeEnabled';
const SIM_PROFILE_KEY = 'voiceBotSimProfile';

const VoiceBotSessionContext = createContext(null);

function readToggle(key) {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function readRegressionMode() {
  if (typeof window === 'undefined') return 'full';
  try {
    const v = (sessionStorage.getItem(REGRESSION_MODE_KEY) || 'full').toLowerCase();
    return v === 'algorithm' ? 'algorithm' : 'full';
  } catch {
    return 'full';
  }
}

export function readBenchToggleFromSession() {
  return readToggle(BENCH_TOGGLE_KEY);
}

export function readRecordToggleFromSession() {
  return readToggle(RECORD_TOGGLE_KEY);
}

export function readReplayToggleFromSession() {
  return readToggle(REPLAY_TOGGLE_KEY);
}

export function readStoredFaceMorph() {
  return readFaceMorph();
}

function readClassifierMode() {
  if (typeof window === 'undefined') return CLASSIFIER_MODES.LEGACY;
  try {
    return normalizeClassifierMode(sessionStorage.getItem(CLASSIFIER_MODE_KEY));
  } catch {
    return CLASSIFIER_MODES.LEGACY;
  }
}

function readRenderFps() {
  if (typeof window === 'undefined') return null;
  try {
    return normalizeRenderFps(sessionStorage.getItem(RENDER_FPS_KEY));
  } catch {
    return null;
  }
}

function readPreferredBoolean(key, preferred, normalize) {
  if (typeof window === 'undefined') return preferred;
  try {
    const raw = sessionStorage.getItem(key);
    if (raw == null) return preferred;
    return normalize(raw);
  } catch {
    return preferred;
  }
}

function readVerifiedMix() {
  return readPreferredBoolean(
    VERIFIED_MIX_KEY,
    PREFERRED_ENGINE_START.verifiedMix,
    normalizeVerifiedMix
  );
}

function readLegacyGearPasser() {
  return readPreferredBoolean(
    LEGACY_GEAR_PASSER_KEY,
    PREFERRED_ENGINE_START.legacyGearPasser,
    normalizeLegacyGearPasser
  );
}

function readLegacyVisemeFilter() {
  return readPreferredBoolean(
    LEGACY_VISEME_FILTER_KEY,
    PREFERRED_ENGINE_START.legacyVisemeFilter,
    normalizeLegacyVisemeFilter
  );
}

function readLegacyConsonantHolder() {
  return readPreferredBoolean(
    LEGACY_CONSONANT_HOLDER_KEY,
    PREFERRED_ENGINE_START.legacyConsonantHolder,
    normalizeLegacyConsonantHolder
  );
}

function readFaceMorph() {
  return readPreferredBoolean(
    FACE_MORPH_KEY,
    PREFERRED_ENGINE_START.faceMorph,
    normalizeFaceMorph
  );
}

function readEnvelopeEnabled() {
  const url = readEnvelopeUrlFlags();
  if (url.envelopeEnabled) return true;
  return readToggle(ENVELOPE_TOGGLE_KEY);
}

function readSimProfile() {
  const url = readEnvelopeUrlFlags();
  if (url.simProfile && url.simProfile !== ENVELOPE_SIM_PROFILES.OFF) {
    return url.simProfile;
  }
  if (typeof window === 'undefined') return ENVELOPE_SIM_PROFILES.OFF;
  try {
    return normalizeSimProfile(sessionStorage.getItem(SIM_PROFILE_KEY));
  } catch {
    return ENVELOPE_SIM_PROFILES.OFF;
  }
}

function resolveEffectiveMode(benchToggleEnabled, recordToggleEnabled, replayToggleEnabled) {
  const urlMode = resolveOperationalMode();
  if (urlMode !== VOICEBOT_MODES.PRODUCTION) {
    return urlMode;
  }
  if (replayToggleEnabled) {
    return VOICEBOT_MODES.REPLAY;
  }
  if (recordToggleEnabled) {
    return VOICEBOT_MODES.RECORD;
  }
  if (benchToggleEnabled) {
    return VOICEBOT_MODES.BENCH;
  }
  return VOICEBOT_MODES.PRODUCTION;
}

function buildSessionState(
  benchToggleEnabled,
  recordToggleEnabled,
  replayToggleEnabled,
  regressionMode,
  classifierMode,
  renderFps,
  verifiedMix,
  legacyGearPasser,
  legacyVisemeFilter,
  legacyConsonantHolder,
  faceMorph,
  envelopeEnabled,
  simProfile
) {
  const mode = resolveEffectiveMode(benchToggleEnabled, recordToggleEnabled, replayToggleEnabled);
  const envelopeOn = !!envelopeEnabled || normalizeSimProfile(simProfile) !== ENVELOPE_SIM_PROFILES.OFF;
  const activation = buildActivation(mode, {
    includePanel:
      mode === VOICEBOT_MODES.BENCH
      || mode === VOICEBOT_MODES.RECORD
      || mode === VOICEBOT_MODES.REPLAY
      || envelopeOn,
    regressionMode,
    classifierMode,
    renderFps,
    verifiedMix,
    legacyGearPasser,
    legacyVisemeFilter,
    legacyConsonantHolder,
    faceMorph,
    envelopeEnabled: envelopeOn,
    simProfile,
  });
  return {
    mode,
    activation,
    benchEnabled: benchToggleEnabled,
    recordEnabled: recordToggleEnabled,
    replayEnabled: replayToggleEnabled,
    regressionMode,
    classifierMode: activation.classifierMode,
    renderFps: activation.renderFps,
    verifiedMix: activation.verifiedMix,
    legacyGearPasser: activation.legacyGearPasser,
    legacyVisemeFilter: activation.legacyVisemeFilter,
    legacyConsonantHolder: activation.legacyConsonantHolder,
    faceMorph: activation.faceMorph,
    envelopeEnabled: envelopeOn,
    simProfile: activation.simProfile,
    simMs: activation.simMs,
  };
}

export function getVoiceBotSession() {
  return buildSessionState(
    readBenchToggleFromSession(),
    readRecordToggleFromSession(),
    readReplayToggleFromSession(),
    readRegressionMode(),
    readClassifierMode(),
    readRenderFps(),
    readVerifiedMix(),
    readLegacyGearPasser(),
    readLegacyVisemeFilter(),
    readLegacyConsonantHolder(),
    readFaceMorph(),
    readEnvelopeEnabled(),
    readSimProfile()
  );
}

export function VoiceBotSessionProvider({ children }) {
  const [benchEnabled, setBenchEnabledState] = useState(readBenchToggleFromSession);
  const [recordEnabled, setRecordEnabledState] = useState(readRecordToggleFromSession);
  const [replayEnabled, setReplayEnabledState] = useState(readReplayToggleFromSession);
  const [regressionMode, setRegressionModeState] = useState(readRegressionMode);
  const [classifierMode, setClassifierModeState] = useState(readClassifierMode);
  const [renderFps, setRenderFpsState] = useState(readRenderFps);
  const [verifiedMix, setVerifiedMixState] = useState(readVerifiedMix);
  const [legacyGearPasser, setLegacyGearPasserState] = useState(readLegacyGearPasser);
  const [legacyVisemeFilter, setLegacyVisemeFilterState] = useState(readLegacyVisemeFilter);
  const [legacyConsonantHolder, setLegacyConsonantHolderState] = useState(readLegacyConsonantHolder);
  const [faceMorph, setFaceMorphState] = useState(readFaceMorph);
  const [envelopeEnabled, setEnvelopeEnabledState] = useState(readEnvelopeEnabled);
  const [simProfile, setSimProfileState] = useState(readSimProfile);

  const setBenchEnabled = useCallback((enabled) => {
    const next = !!enabled;
    try {
      sessionStorage.setItem(BENCH_TOGGLE_KEY, next ? '1' : '0');
    } catch {
      /* optional */
    }
    setBenchEnabledState(next);
  }, []);

  const setRecordEnabled = useCallback((enabled) => {
    const next = !!enabled;
    try {
      sessionStorage.setItem(RECORD_TOGGLE_KEY, next ? '1' : '0');
      if (next) sessionStorage.setItem(REPLAY_TOGGLE_KEY, '0');
    } catch {
      /* optional */
    }
    setRecordEnabledState(next);
    if (next) setReplayEnabledState(false);
  }, []);

  const setReplayEnabled = useCallback((enabled) => {
    const next = !!enabled;
    try {
      sessionStorage.setItem(REPLAY_TOGGLE_KEY, next ? '1' : '0');
      if (next) sessionStorage.setItem(RECORD_TOGGLE_KEY, '0');
    } catch {
      /* optional */
    }
    setReplayEnabledState(next);
    if (next) setRecordEnabledState(false);
  }, []);

  const setRegressionMode = useCallback((mode) => {
    const next = mode === 'algorithm' ? 'algorithm' : 'full';
    try {
      sessionStorage.setItem(REGRESSION_MODE_KEY, next);
    } catch {
      /* optional */
    }
    setRegressionModeState(next);
  }, []);

  const setClassifierMode = useCallback((nextMode) => {
    const next = normalizeClassifierMode(nextMode);
    try {
      if (next === CLASSIFIER_MODES.LEGACY) sessionStorage.removeItem(CLASSIFIER_MODE_KEY);
      else sessionStorage.setItem(CLASSIFIER_MODE_KEY, next);
    } catch {
      /* optional */
    }
    setClassifierModeState(next);
  }, []);

  const setRenderFps = useCallback((nextFps) => {
    const next = normalizeRenderFps(nextFps);
    try {
      if (next == null) sessionStorage.removeItem(RENDER_FPS_KEY);
      else sessionStorage.setItem(RENDER_FPS_KEY, String(next));
    } catch {
      /* optional */
    }
    setRenderFpsState(next);
  }, []);

  const persistPreferredBoolean = useCallback((key, preferred, setter, nextValue) => {
    const next = !!nextValue;
    try {
      if (next === preferred) sessionStorage.removeItem(key);
      else sessionStorage.setItem(key, next ? '1' : '0');
    } catch {
      /* optional */
    }
    setter(next);
  }, []);

  const setVerifiedMix = useCallback((nextValue) => {
    persistPreferredBoolean(
      VERIFIED_MIX_KEY,
      PREFERRED_ENGINE_START.verifiedMix,
      setVerifiedMixState,
      normalizeVerifiedMix(nextValue)
    );
  }, [persistPreferredBoolean]);

  const setLegacyGearPasser = useCallback((nextValue) => {
    persistPreferredBoolean(
      LEGACY_GEAR_PASSER_KEY,
      PREFERRED_ENGINE_START.legacyGearPasser,
      setLegacyGearPasserState,
      normalizeLegacyGearPasser(nextValue)
    );
  }, [persistPreferredBoolean]);

  const setLegacyVisemeFilter = useCallback((nextValue) => {
    persistPreferredBoolean(
      LEGACY_VISEME_FILTER_KEY,
      PREFERRED_ENGINE_START.legacyVisemeFilter,
      setLegacyVisemeFilterState,
      normalizeLegacyVisemeFilter(nextValue)
    );
  }, [persistPreferredBoolean]);

  const setLegacyConsonantHolder = useCallback((nextValue) => {
    persistPreferredBoolean(
      LEGACY_CONSONANT_HOLDER_KEY,
      PREFERRED_ENGINE_START.legacyConsonantHolder,
      setLegacyConsonantHolderState,
      normalizeLegacyConsonantHolder(nextValue)
    );
  }, [persistPreferredBoolean]);

  const setFaceMorph = useCallback((nextValue) => {
    persistPreferredBoolean(
      FACE_MORPH_KEY,
      PREFERRED_ENGINE_START.faceMorph,
      setFaceMorphState,
      normalizeFaceMorph(nextValue)
    );
  }, [persistPreferredBoolean]);

  const setEnvelopeEnabled = useCallback((enabled) => {
    const next = !!enabled;
    try {
      sessionStorage.setItem(ENVELOPE_TOGGLE_KEY, next ? '1' : '0');
    } catch {
      /* optional */
    }
    setEnvelopeEnabledState(next);
  }, []);

  const setSimProfile = useCallback((nextProfile) => {
    const next = normalizeSimProfile(nextProfile);
    try {
      if (next === ENVELOPE_SIM_PROFILES.OFF) sessionStorage.removeItem(SIM_PROFILE_KEY);
      else sessionStorage.setItem(SIM_PROFILE_KEY, next);
    } catch {
      /* optional */
    }
    setSimProfileState(next);
  }, []);

  useEffect(() => {
    const journey = isJourneyArmed();
    const envelopeOn = envelopeEnabled || simProfile !== ENVELOPE_SIM_PROFILES.OFF || journey;
    if (envelopeOn) startVoiceBotEnvelope({ journey });
    startVoiceBotSimHarness(simProfile);
    return () => stopVoiceBotSimHarness();
  }, [envelopeEnabled, simProfile]);

  const session = useMemo(
    () => ({
      ...buildSessionState(
        benchEnabled,
        recordEnabled,
        replayEnabled,
        regressionMode,
        classifierMode,
        renderFps,
        verifiedMix,
        legacyGearPasser,
        legacyVisemeFilter,
        legacyConsonantHolder,
        faceMorph,
        envelopeEnabled,
        simProfile
      ),
      setBenchEnabled,
      setRecordEnabled,
      setReplayEnabled,
      setRegressionMode,
      setClassifierMode,
      setRenderFps,
      setVerifiedMix,
      setLegacyGearPasser,
      setLegacyVisemeFilter,
      setLegacyConsonantHolder,
      setFaceMorph,
      setEnvelopeEnabled,
      setSimProfile,
    }),
    [
      benchEnabled,
      recordEnabled,
      replayEnabled,
      regressionMode,
      classifierMode,
      renderFps,
      verifiedMix,
      legacyGearPasser,
      legacyVisemeFilter,
      legacyConsonantHolder,
      faceMorph,
      envelopeEnabled,
      simProfile,
      setBenchEnabled,
      setRecordEnabled,
      setReplayEnabled,
      setRegressionMode,
      setClassifierMode,
      setRenderFps,
      setVerifiedMix,
      setLegacyGearPasser,
      setLegacyVisemeFilter,
      setLegacyConsonantHolder,
      setFaceMorph,
      setEnvelopeEnabled,
      setSimProfile,
    ]
  );

  return (
    <VoiceBotSessionContext.Provider value={session}>
      {children}
    </VoiceBotSessionContext.Provider>
  );
}

export function useVoiceBotSession() {
  const context = useContext(VoiceBotSessionContext);
  return context || getVoiceBotSession();
}
